/* eslint-disable jsdoc/require-returns */
/* eslint-disable jsdoc/require-param-type */

/**
 * PieChart Component
 *
 * A reusable pie/donut chart component built with Recharts.
 * Supports custom colors, interactive hover states, and flexible tooltips.
 *
 * @example
 * // Basic pie chart
 * <PieChart
 *   data={[
 *     { name: 'Segment A', value: 35 },
 *     { name: 'Segment B', value: 30 },
 *     { name: 'Segment C', value: 20 },
 *   ]}
 *   formatValue={(v) => `${v}%`}
 * />
 *
 * @example
 * // Donut chart with custom colors
 * <PieChart
 *   data={[
 *     { label: 'Category 1', amount: 100, color: '#3B82F6' },
 *     { label: 'Category 2', amount: 75, color: '#10B981' },
 *   ]}
 *   nameKey="label"
 *   valueKey="amount"
 *   colorKey="color"
 *   variant="donut"
 * />
 */

import { useCallback, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
  Sector
} from 'recharts';

import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';

import * as Styled from '../Charts.style';
import { getChartColors } from '../utils/colors';
import { identity } from '../utils/formatters';
/**
 * Active shape renderer for hover effect
 * @param props
 */
const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;

  return (
    <Sector
      cx={cx}
      cy={cy}
      endAngle={endAngle}
      fill={fill}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 8}
      startAngle={startAngle}
    />
  );
};

/**
 * Custom tooltip component
 * @param root0
 * @param root0.active
 * @param root0.payload
 * @param root0.formatTooltip
 * @param root0.formatValue
 */
const CustomTooltip = ({ active, payload, formatTooltip, formatValue }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload;

  if (!item) {
    return null;
  }

  // Use custom formatter if provided
  if (formatTooltip) {
    const tooltipData = formatTooltip(item);

    return (
      <Styled.TooltipContainer>
        {tooltipData.title && (
          <Styled.TooltipTitle size="xs">{tooltipData.title}</Styled.TooltipTitle>
        )}
        {Object.entries(tooltipData)
          .filter(([key]) => key !== 'title' && key !== 'badge')
          .map(([label, value]) => (
            <Styled.TooltipRow key={label}>
              <Text size="xs">{label}:</Text>
              <Text size="xs" weight="medium">
                {value}
              </Text>
            </Styled.TooltipRow>
          ))}
        {tooltipData.badge && (
          <Styled.TooltipBadge color={item.fill} size="xs">
            {tooltipData.badge}
          </Styled.TooltipBadge>
        )}
      </Styled.TooltipContainer>
    );
  }

  // Default tooltip
  return (
    <Styled.TooltipContainer>
      <Styled.TooltipTitle size="xs">{item.name}</Styled.TooltipTitle>
      <Styled.TooltipRow>
        <Text size="xs">Value:</Text>
        <Text size="xs" weight="medium">
          {formatValue(item.value)}
        </Text>
      </Styled.TooltipRow>
    </Styled.TooltipContainer>
  );
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  formatTooltip: PropTypes.func,
  formatValue: PropTypes.func
};

/**
 * PieChart Component
 * @param root0
 * @param root0.data
 * @param root0.nameKey
 * @param root0.valueKey
 * @param root0.colorKey
 * @param root0.colors
 * @param root0.formatValue
 * @param root0.formatTooltip
 * @param root0.variant
 * @param root0.height
 * @param root0.showLegend
 * @param root0.showLabels
 * @param root0.legendPosition
 * @param root0.title
 * @param root0.animationDuration
 */
const PieChart = ({
  // Data
  data = [],
  nameKey = 'name',
  valueKey = 'value',
  colorKey = null,

  // Colors (used when colorKey not provided)
  colors = null,

  // Formatting
  formatValue = identity,
  formatTooltip = null, // eslint-disable-line no-unused-vars

  // Variant
  variant = 'pie',

  // Layout
  height = 350,
  showLegend = true,
  showLabels = true,
  legendPosition = 'bottom',
  title = '',

  // Interaction
  animationDuration = 400
}) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const [activeIndex, setActiveIndex] = useState(null);

  // Memoize processed data with colors
  const chartData = useMemo(() => {
    const colorPalette = colors || chartColors.series;

    return data.map((item, index) => ({
      ...item,
      name: item[nameKey],
      value: item[valueKey],
      fill: colorKey ? item[colorKey] : colorPalette[index % colorPalette.length]
    }));
  }, [data, nameKey, valueKey, colorKey, colors, chartColors.series]);

  const onPieEnter = useCallback((_, index) => {
    setActiveIndex(index);
  }, []);

  const onPieLeave = useCallback(() => {
    setActiveIndex(null);
  }, []);

  // Empty state
  if (!data?.length) {
    return (
      <Styled.ChartWrapper>
        {title && <Styled.ChartTitle>{title}</Styled.ChartTitle>}
        <Styled.EmptyState>No data available</Styled.EmptyState>
      </Styled.ChartWrapper>
    );
  }

  // Calculate total for percentage
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  // Custom label renderer
  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
    if (!showLabels) {
      return null;
    }

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const percent = value / total;

    // Don't show label for small segments
    if (percent < 0.08) {
      return null;
    }

    return (
      <text
        dominantBaseline="central"
        fill="white"
        fontSize={12}
        fontWeight="bold"
        textAnchor="middle"
        x={x}
        y={y}
      >
        {formatValue(value)}
      </text>
    );
  };

  // Custom legend renderer
  const renderLegend = ({ payload }) => (
    <Styled.LegendContainer
      style={{
        flexDirection: legendPosition === 'right' ? 'column' : 'row',
        alignItems: legendPosition === 'right' ? 'flex-start' : 'center'
      }}
    >
      {payload.map((entry) => {
        const item = chartData.find((d) => d.name === entry.value);

        return (
          <Styled.LegendItem key={entry.value}>
            <Styled.LegendColor style={{ backgroundColor: entry.color }} />
            <Styled.LegendText>
              {entry.value} ({formatValue(item?.value || 0)})
            </Styled.LegendText>
          </Styled.LegendItem>
        );
      })}
    </Styled.LegendContainer>
  );

  // Pie dimensions based on variant
  const innerRadius = variant === 'donut' ? 60 : 0;
  const outerRadius = variant === 'donut' ? 100 : 110;

  return (
    <Styled.ChartWrapper>
      {title && <Styled.ChartTitle>{title}</Styled.ChartTitle>}
      <ResponsiveContainer height={height} width="100%">
        <RechartsPieChart>
          <Pie
            activeIndex={activeIndex}
            activeShape={renderActiveShape}
            animationDuration={animationDuration}
            cx="50%"
            cy="50%"
            data={chartData}
            dataKey="value"
            innerRadius={innerRadius}
            label={showLabels ? renderLabel : false}
            labelLine={false}
            nameKey="name"
            outerRadius={outerRadius}
            paddingAngle={variant === 'donut' ? 2 : 1}
            onMouseEnter={onPieEnter}
            onMouseLeave={onPieLeave}
          >
            {chartData.map((entry) => (
              <Cell key={entry.name} fill={entry.fill} stroke="white" strokeWidth={2} />
            ))}
          </Pie>

          {/* <Tooltip
            content={<CustomTooltip formatTooltip={formatTooltip} formatValue={formatValue} />}
          /> */}

          {showLegend && (
            <Legend
              align={legendPosition === 'right' ? 'right' : 'center'}
              content={renderLegend}
              layout={legendPosition === 'right' ? 'vertical' : 'horizontal'}
              verticalAlign={legendPosition === 'right' ? 'middle' : 'bottom'}
            />
          )}
        </RechartsPieChart>
      </ResponsiveContainer>
    </Styled.ChartWrapper>
  );
};

PieChart.propTypes = {
  /** Array of data objects */
  data: PropTypes.arrayOf(PropTypes.object),
  /** Key for segment names */
  nameKey: PropTypes.string,
  /** Key for segment values */
  valueKey: PropTypes.string,
  /** Key for per-item colors (optional) */
  colorKey: PropTypes.string,
  /** Array of colors to use (defaults to theme palette) */
  colors: PropTypes.arrayOf(PropTypes.string),
  /** Formatter for value display */
  formatValue: PropTypes.func,
  /** Custom tooltip formatter: (dataItem) => { title, ...labels, badge } */
  formatTooltip: PropTypes.func,
  /** Chart variant */
  variant: PropTypes.oneOf(['pie', 'donut']),
  /** Chart height in pixels */
  height: PropTypes.number,
  /** Show legend */
  showLegend: PropTypes.bool,
  /** Show percentage labels on slices */
  showLabels: PropTypes.bool,
  /** Legend position */
  legendPosition: PropTypes.oneOf(['bottom', 'right']),
  /** Chart title */
  title: PropTypes.string,
  /** Animation duration in ms */
  animationDuration: PropTypes.number
};

export default PieChart;
